


/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import {
    Checkbox,
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    EventDefinitionContext,
    Field,
    FormBreadcrumbs,
    FormErrorDisplayMode,
    FormInputOptionsContext,
    FormLookupsContext,
    FormOptionsContext,
    getParentPropertyPath,
    GroupedField,
    GroupedFieldErrorFlag,
    GroupedFields,
    GroupedTableArrayField,
    ICrfConditionParameters,
    IFormInputOptionsContext,
    IGroupedTableArrayFieldColumn,
    Input,
    InputOptionMode,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
    ToggleButtonGroup
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { FC, useContext, useMemo } from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { Box, Typography } from '@mui/material';
import { ConditionMode, InputComponentType, useFieldActions, ValidateOn } from '@ngt/forms-core';
import * as Dtos from '../../../api/dtos';
import { AppSettings } from '../../../AppSettings';
import useProcomidaProFormLookup from '../../../hooks/lookups/useProcomidaProFormLookup';


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */
interface IProcomidaProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Constants
 * ----------------------------------------------------------------------------------
 */

const incompleteReasonOther = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.Procomida>) => { 
    const regex = /incompleteForms\[(?<Index>[0-9]*)].(?<GroupedField>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    return formState?.value?.incompleteForms?.[+idx]?.other ?? false;
};

const assistanceOther = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.Procomida>) => { 
    const regex = /assistedForms\[(?<Index>[0-9]*)].(?<GroupedField>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    return formState?.value?.assistedForms?.[+idx]?.assistanceOther ?? false;
};

const assistanceReasonOther = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.Procomida>) => { 
    const regex = /assistedForms\[(?<Index>[0-9]*)].(?<GroupedField>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    return formState?.value?.assistedForms?.[+idx]?.reasonOther ?? false;
};

const assistanceAssistedOther = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.Procomida>) => { 
    const regex = /assistedForms\[(?<Index>[0-9]*)].(?<GroupedField>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    return formState?.value?.assistedForms?.[+idx]?.assistedByOther ?? false;
};

const inputOptions: IFormInputOptionsContext = {
    labelMode: InputOptionMode.Auto,
    errorMode: InputOptionMode.Hide
};

const InTableField: FC<{ component: InputComponentType, hideErrors: boolean; }> = ({
    hideErrors,
    component
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center'
            }}
        >
            <Box
                sx={{
                    flex: '1 1 auto',
                    width: hideErrors ? '100%' : 'calc(100% - 32px)'
                }}
            >
                <Input
                    component={component}
                />
            </Box>
            {
                !hideErrors && (
                    <Box
                        sx={{
                            flex: '0 0 auto',
                            minWidth: 32,
                            maxWidth: 32
                        }}
                    >
                        <GroupedFieldErrorFlag />
                    </Box>
                )
            }
        </Box>
    )
}

const IncompleteReasons: React.FunctionComponent = () => {
    const { getPropertyPath } = useFieldActions();

    const parentName = useMemo(() => {
        return getParentPropertyPath(getPropertyPath()) ?? ''
    }, [getPropertyPath])
    
    const { errorMode: formErrorMode } = useContext(FormOptionsContext);

    const hideErrors = formErrorMode === FormErrorDisplayMode.AlwaysHideErrors;

    return (
        <FormInputOptionsContext.Provider value={inputOptions}>
            <Field propertyPath={`${parentName}.patientRefusal`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.patientIll`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.other`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.otherSpecify`} prependParentPropertyPath={false}>
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={incompleteReasonOther}
                    subscription={{ value: true }}
                >
                    <InTableField component={Text} hideErrors={hideErrors} />
                </CrfCondition>
            </Field>
        </FormInputOptionsContext.Provider>
    );
}

const PatientAssistance: React.FunctionComponent = () => {
    const { getPropertyPath } = useFieldActions();

    const parentName = useMemo(() => {
        return getParentPropertyPath(getPropertyPath()) ?? ''
    }, [getPropertyPath])
    
    const { errorMode: formErrorMode } = useContext(FormOptionsContext);

    const hideErrors = formErrorMode === FormErrorDisplayMode.AlwaysHideErrors;

    return (
        <FormInputOptionsContext.Provider value={inputOptions}>
            <Field propertyPath={`${parentName}.assistanceReadAllowed`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.assistanceClarificationRequired`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.assistanceIndependentlyCompleted`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.assistanceOther`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.assistanceOtherSpecify`} prependParentPropertyPath={false}>
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={assistanceOther}
                    subscription={{ value: true }}
                >
                    <InTableField component={Text} hideErrors={hideErrors} />
                </CrfCondition>
            </Field>
        </FormInputOptionsContext.Provider>
    );
}

const AssistanceReason: React.FunctionComponent = () => {
    const { getPropertyPath } = useFieldActions();

    const parentName = useMemo(() => {
        return getParentPropertyPath(getPropertyPath()) ?? ''
    }, [getPropertyPath])
    
    const { errorMode: formErrorMode } = useContext(FormOptionsContext);

    const hideErrors = formErrorMode === FormErrorDisplayMode.AlwaysHideErrors;

    return (
        <FormInputOptionsContext.Provider value={inputOptions}>
            <Field propertyPath={`${parentName}.reasonCouldNotRead`} prependParentPropertyPath={false}>
            <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.reasonOther`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.reasonOtherSpecify`} prependParentPropertyPath={false}>
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={assistanceReasonOther}
                    subscription={{ value: true }}
                >
                    <Input
                        component={Text}
                    />
                </CrfCondition>
            </Field>
        </FormInputOptionsContext.Provider>
    );
}

const AssistedBy: React.FunctionComponent = () => {
    const { getPropertyPath } = useFieldActions();

    const parentName = useMemo(() => {
        return getParentPropertyPath(getPropertyPath()) ?? ''
    }, [getPropertyPath])
    
    const { errorMode: formErrorMode } = useContext(FormOptionsContext);

    const hideErrors = formErrorMode === FormErrorDisplayMode.AlwaysHideErrors;


    return (
        <FormInputOptionsContext.Provider value={inputOptions}>
            <Field propertyPath={`${parentName}.assistedByStaff`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.assistedByFamily`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.assistedByFriend`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.assistedByOther`} prependParentPropertyPath={false}>
                <InTableField component={Checkbox} hideErrors={hideErrors} />
            </Field>
            <Field propertyPath={`${parentName}.assistedByOtherSpecify`} prependParentPropertyPath={false}>
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={assistanceAssistedOther}
                    subscription={{ value: true }}
                >
                    <InTableField component={Text} hideErrors={hideErrors} />
                </CrfCondition>
            </Field>
        </FormInputOptionsContext.Provider>
    );
}

const ProcomidaIncompleteColumns: Array<IGroupedTableArrayFieldColumn<Dtos.ProcomidaIncomplete>> = [
    {
        propertyPath: 'formDefinitionId',
        TableCellProps: {
            sx: {
                minWidth: 350,
                //width: 350
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'id',
        headerLabel: 'Indicate primary reason(s) why form was not completed:' as any,
        TableCellProps: {
            sx: {
                minWidth: 300,
                maxWidth: 300,
                width: 300
            }
        },
        content: (
            <>
                <IncompleteReasons/>
            </>
        )
    }
];

const ProcomidaAssistedColumns: Array<IGroupedTableArrayFieldColumn<Dtos.ProcomidaIncomplete>> = [
    {
        propertyPath: 'formDefinitionId',
        TableCellProps: {
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'id',
        headerLabel: 'Indicate how the patient was assisted:' as any,
        TableCellProps: {
            sx: {
                minWidth: 350,
                maxWidth: 350,
                width: 350
            }
        },
        content: (
            <>
                <PatientAssistance/>
            </>
        )
    },
    {
        propertyPath: 'id',
        headerLabel: 'Indicate the primary reason(s) for assistance:' as any,
        TableCellProps: {
            sx: {
                minWidth: 350,
                maxWidth: 350,
                width: 350
            }
        },
        content: (
            <>
                <AssistanceReason/>
            </>
        )
    },
    {
        propertyPath: 'id',
        TableCellProps: {
            sx: {
                
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        headerLabel: 'Indicate who assisted or completed the assessment:' as any,
        content: (
            <>
                <AssistedBy/>
            </>
        )
    }
];

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const hasIncompletePros = ({ formState }: ICrfConditionParameters<Dtos.Procomida>) => formState?.value?.allProsCompleted === Dtos.LookupYesNoType.No;
const hasAssistedPros = ({ formState }: ICrfConditionParameters<Dtos.Procomida>) => formState?.value?.patientRequiredAssistance === Dtos.LookupYesNoType.Yes;


/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const Procomida: React.FunctionComponent<IProcomidaProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    const { data: eventDefinition } = useContext(EventDefinitionContext);

    const {
        data: proFormLookupItems,
        loading: proFormLookupLoading
    } = useProcomidaProFormLookup(patient?.id, eventDefinition?.id);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const formDefinitionLookups = useMemo(() => {
        return [
            {
                propertyName: `incompleteForms.formDefinitionId`,
                items: proFormLookupItems ?? []
            },
            {
                propertyName: `assistedForms.formDefinitionId`,
                items: proFormLookupItems ?? []
            }
        ];
    }, [proFormLookupItems]);

    if (permissionsLoading || proFormLookupLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings.debug}
            >
                <GroupedFields>
                    <GroupedField
                        propertyPath="allProsCompleted"
                        component={ToggleButtonGroup}
                    />
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={hasIncompletePros}
                        subscription={{ value: true }}
                    >
                        <Typography
                            variant="h3"
                            sx={{p: 2}}
                        >
                            Incomplete Forms
                        </Typography>
                        <FormLookupsContext.Provider value={formDefinitionLookups}>
                            <GroupedTableArrayField
                                propertyPath={`incompleteForms`}
                                columns={ProcomidaIncompleteColumns}
                                entityName="form"
                                AddButtonProps={{
                                    sx: {
                                        mb: 2
                                    }
                                }}
                            />
                        </FormLookupsContext.Provider>
                    </CrfCondition>
                </GroupedFields>
                <GroupedFields>
                    <GroupedField
                        propertyPath="patientRequiredAssistance"
                        component={ToggleButtonGroup}
                    />

                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={hasAssistedPros}
                        subscription={{ value: true }}
                    >
                        <Typography
                            variant="h2"
                            sx={{p: 2}}
                        >
                            Assisted Forms
                        </Typography>
                        <FormLookupsContext.Provider value={formDefinitionLookups}>
                            <GroupedTableArrayField
                                propertyPath={`assistedForms`}
                                columns={ProcomidaAssistedColumns}
                                entityName="form"
                                AddButtonProps={{
                                    sx: {
                                        mb: 2
                                    }
                                }}
                            />
                        </FormLookupsContext.Provider>
                    </CrfCondition>
                </GroupedFields>
            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default Procomida;
